import * as React from 'react';
import { map } from 'lodash';

import { Table, Typography } from '@revfluence/fresh';

import { LoadSpinner } from '@frontend/lib';
import { IClient, useGetEmailResourcesForClientQuery } from '@frontend/app/hooks';
import ActionCell from './ActionCell';
import ActiveGrantsCell, { IActiveGrant } from './ActiveGrantsCell';

const { useMemo } = React;

const { Text } = Typography;

interface TableDataType {
  resourceId: number;
  email: string;
  isShared: boolean;
  activeGrants: IActiveGrant[];
}

interface IClientConnectedEmailsProps {
  className?: string;
  clientId: string;
  client: IClient;
}

const ClientConnectedEmails: React.FC<React.PropsWithChildren<IClientConnectedEmailsProps>> = React.memo((props) => {
  const { data, loading, error, refetch } = useGetEmailResourcesForClientQuery({
    variables: {
      clientId: props.clientId,
    },
    fetchPolicy: 'no-cache',
  });
  const onChanged = React.useCallback(() => {
    refetch();
  }, [refetch]);
  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
    },
    {
      title: 'Shared',
      dataIndex: 'isShared',
      render: (isShared: boolean) => (isShared ? 'Yes' : 'No'),
    },
    {
      title: 'Active Grants',
      key: 'activeGrants',
      render: (record: TableDataType) => (
        <ActiveGrantsCell
          grants={record.activeGrants}
          clientId={props.clientId}
          users={props.client?.users}
          onRevoked={onChanged}
        />
      ),
      width: 260,
    },
    {
      title: 'Action',
      key: 'action',
      render: (record: TableDataType) => (
        <ActionCell
          resourceId={record.resourceId}
          email={record.email}
          isShared={record.isShared}
          clientId={props.clientId}
          users={props.client?.users}
          onGranted={onChanged}
        />
      ),
      width: 260,
    },
  ];
  const dataSource: TableDataType[] = useMemo(
    () =>
      map(data?.resources || [], (resource) => ({
        resourceId: resource.id,
        email: resource.authProvider.userExternalDisplayId,
        isShared: resource.authProvider.isShared,
        activeGrants: resource.activeGrants,
      })),
    [data],
  );

  if (loading) return <LoadSpinner />;
  if (error) return <Text type="danger">Failed to load connected emails. Please try refreshing.</Text>;
  return (
    <div className={props.className}>
      {dataSource.length ? (
        <Table rowKey="resourceId" columns={columns} dataSource={dataSource} pagination={false} />
      ) : (
        <Text>No connected emails</Text>
      )}
    </div>
  );
});
ClientConnectedEmails.displayName = 'ClientConnectedEmails';

export default ClientConnectedEmails;
